<template>
  <div class="max-w-7xl mx-auto mt-8 bg-white p-6 rounded-xl shadow-md">
    <!-- Header -->
    <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-4">
      <div class="min-w-0">
        <h2 class="text-2xl font-bold text-gray-800">Prompt versions</h2>
        <p class="text-sm text-gray-500 mt-1">
          Jede Änderung an einem Bewertungsprompt wird als Version gespeichert. Wirksam
          wird sie erst nach einem bestandenen Regressionsvergleich.
        </p>

        <!-- ⚠️ Welcher der beiden Schlüssel gerade bewertet. Diese Zeile ist
             keine Zierde: die Liste darunter führt beide nebeneinander, und
             ohne sie ist einer Version nicht anzusehen, ob sie nach dem
             Aktivieren überhaupt etwas ändert. Sie steht deshalb als Text und
             nicht in einem Tooltip — die Einordnung wird bei JEDEM Blick auf
             die Liste gebraucht, nicht einmal.

             Kein Platzhalter, solange nichts geladen ist: ein geratener Wert
             stünde hier als Tatsachenbehauptung. -->
        <p v-if="wirksam" class="text-sm mt-1.5 flex flex-wrap items-center gap-1.5">
          <template v-if="wirksamerSchluessel">
            <span class="text-gray-600">Bewertet wird gerade mit</span>
            <code class="px-1.5 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs">{{
                wirksamerSchluessel
              }}</code>
            <span class="text-gray-500 text-xs">(Modus {{ wirksam.rating_mode }})</span>
            <BaseTooltip :text="HILFE_WIRKSAM" :oben="false" fixiert>
              <InformationCircleIcon class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
            </BaseTooltip>
          </template>
          <template v-else>
            <span class="inline-flex items-center gap-1.5 text-amber-800 font-semibold">
              Modus {{ wirksam.rating_mode }} — es wird ohne Prompt bewertet
            </span>
            <BaseTooltip :text="HILFE_MODUS_ML" :oben="false" fixiert>
              <InformationCircleIcon class="w-4 h-4 text-amber-600 hover:text-amber-800 cursor-help" />
            </BaseTooltip>
          </template>
        </p>

        <!-- Der Ablauf einer Prompt-Änderung, als drei Schritte statt als
             Textblock. Die vollen Erklärungen liegen in den Tooltips: sie
             werden EINMAL gebraucht, danach steht der Ablauf im Kopf und der
             Block schob die Versionsliste bei jedem Besuch nach unten. Der
             erste Schritt ist zugleich der Weg dorthin -- er ist der einzige,
             an dem es hier etwas zu klicken gibt. -->
        <div class="flex flex-wrap items-center gap-1.5 mt-3">
          <template v-for="(schritt, i) in WEG" :key="schritt.titel">
            <span v-if="i" class="text-gray-300 text-xs" aria-hidden="true">→</span>
            <BaseTooltip :text="schritt.hilfe" :oben="false" fixiert>
              <component
                  :is="schritt.ziel ? 'router-link' : 'span'"
                  :to="schritt.ziel"
                  class="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs
                         border-gray-200 bg-gray-50 text-gray-700"
                  :class="schritt.ziel ? 'hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700' : 'cursor-help'"
              >
                <span class="font-mono text-[10px] text-gray-400">{{ i + 1 }}</span>
                {{ schritt.titel }}
              </component>
            </BaseTooltip>
          </template>

          <BaseTooltip :text="HILFE_KOMMANDOZEILE" :oben="false" fixiert>
            <InformationCircleIcon class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
          </BaseTooltip>
        </div>
      </div>

      <div class="flex shrink-0 flex-wrap items-center gap-2">
        <!-- Sichtbares Zeichen, dass die Seite von selbst nachfragt. Ohne das
             ist der stille Poll nicht von einer stehengebliebenen Seite zu
             unterscheiden, und man drückt reflexhaft Reload. -->
        <BaseTooltip
            v-if="hasRunningComparison"
            :text="`Ein Vergleich läuft. Die Seite fragt den Stand alle ${Math.round(POLL_MS / 1000)} Sekunden von selbst nach — Reload ist dafür nicht nötig.`"
        >
          <span class="inline-flex items-center gap-1.5 text-xs text-amber-700 cursor-help">
            <span class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" aria-hidden="true"></span>
            aktualisiert automatisch
          </span>
        </BaseTooltip>

        <select
            v-model="promptKey"
            class="px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            @change="reload"
        >
          <option value="">Alle Schlüssel</option>
          <option value="llm_prompt_template">llm_prompt_template</option>
          <option value="rag_prompt_template">rag_prompt_template</option>
        </select>

        <BaseTooltip text="Liste neu laden">
          <BaseButton
              variant="neutral"
              size="icon"
              type="button"
              aria-label="Liste neu laden"
              :aria-busy="loading ? 'true' : 'false'"
              :disabled="loading"
              @click="reload"
          >
            <ArrowPathIcon class="w-5 h-5" :class="loading ? 'animate-spin' : ''" />
          </BaseButton>
        </BaseTooltip>
      </div>
    </div>

    <!-- Bootstrap-Warnung: ohne aktive Version geht nichts.
         Bleibt sichtbar und wandert NICHT in einen Tooltip -- sie beschreibt
         keinen Hintergrund, sondern eine Sackgasse, aus der nur der Befehl
         darunter herausführt. Das Warum steht daneben, weil es sich einmal
         liest und danach nur noch im Weg steht. -->
    <div
        v-if="!loading && !hasActiveVersion"
        class="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-900 text-sm"
    >
      <div class="flex items-center gap-1.5 font-semibold">
        Für {{ missingBaselineLabel }} ist keine Version aktiv
        <BaseTooltip :text="HILFE_BOOTSTRAP" :oben="false" fixiert>
          <InformationCircleIcon class="w-4 h-4 text-amber-600 hover:text-amber-800 cursor-help" />
        </BaseTooltip>
      </div>
      <p class="text-xs mt-1">
        Ohne aktive Version kann keine freigegeben werden — der Vergleich braucht eine, gegen
        die er misst. Die erste ist deshalb ein einmaliger Bootstrap über die Kommandozeile:
      </p>
      <!-- ⚠️ Der Schlüssel im Befehl ist BERECHNET, nicht getippt. Hier stand
           fest 'llm_prompt_template', während die Überschrift darüber den
           tatsächlich fehlenden nannte — wer den Befehl kopiert, legt dann
           eine Baseline für den falschen Schlüssel an, und die Sackgasse, aus
           der er herausführen sollte, besteht danach unverändert fort. -->
      <pre class="mt-2 p-2 bg-white border border-amber-200 rounded text-xs font-mono overflow-x-auto">docker compose exec backend \
  python scripts/prompt_gate.py baseline --key {{ bootstrapSchluessel }}</pre>
    </div>

    <div v-if="error" class="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
      {{ error }}
    </div>

    <div v-if="savedMessage" class="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
      {{ savedMessage }}
    </div>

    <!-- Versionsliste -->
    <ul class="divide-y divide-gray-200">
      <li v-for="v in versions" :key="v.id" class="py-4">
        <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-lg font-medium text-gray-900">
                v{{ v.version }}
              </span>
              <!-- ⚠️ Der Schlüssel trägt jetzt eine Aussage und ist nicht mehr
                   bloß eine graue Angabe: gehört die Version nicht zum
                   wirksamen Schlüssel, ändert ihre Aktivierung an der
                   laufenden Bewertung nichts. Der Vergleich läuft trotzdem
                   sauber durch (er leitet den Modus aus dem SCHLÜSSEL ab),
                   besteht, und die Version wird aktiv — ohne jede Wirkung. -->
              <BaseTooltip v-if="wirktNicht(v)" :text="hilfeWirktNicht(v)" fixiert>
                <span
                    class="inline-flex items-center gap-1 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 cursor-help"
                >
                  {{ v.prompt_key }}
                  <span class="text-xs font-semibold">wirkt nicht</span>
                </span>
              </BaseTooltip>
              <span v-else class="text-sm text-gray-500">{{ v.prompt_key }}</span>

              <BaseTooltip :text="statusHilfe(v.gate_status)" fixiert>
                <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold cursor-help"
                    :class="statusClass(v.gate_status)"
                >
                  {{ v.gate_status }}
                </span>
              </BaseTooltip>

              <BaseTooltip
                  v-if="v.is_active"
                  text="Mit dieser Version bewertet der RatingWorker jede neue Nachricht. Je Schlüssel ist genau eine Version aktiv."
                  fixiert
              >
                <span
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 cursor-help"
                >
                  aktiv
                </span>
              </BaseTooltip>

              <!-- R7: woher der Text stammt. Ohne diese Marke ist einer
                   Version nicht anzusehen, dass hinter ihr eine menschliche
                   Korrektur steht -- und genau die Kette ist der Gegenstand
                   der Reflektionsseite. -->
              <BaseTooltip
                  v-if="ausReflektion(v)"
                  :text="HILFE_REFLEKTION"
                  fixiert
              >
                <router-link
                    to="/async/reflection"
                    class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200"
                >
                  aus Reflektion
                </router-link>
              </BaseTooltip>
            </div>

            <div class="text-xs text-gray-500 mt-1">
              angelegt {{ formatTs(v.created_at) }}
              <span v-if="v.activated_at">
                · zuletzt aktiviert {{ formatTs(v.activated_at) }}
                <span v-if="v.activated_by">von {{ v.activated_by }}</span>
              </span>
              ·
              <BaseTooltip
                  :text="`sha256 des Prompt-Textes, vollständig:\n${v.content_hash || '—'}\n\nDer Hash ist der Grund, aus dem ein zweimal gespeicherter Text keine zweite Version erzeugt.`"
                  :oben="false"
                  fixiert
              >
                <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">sha256
                  {{ (v.content_hash || "").slice(0, 12) }}</span>
              </BaseTooltip>
              <!-- ⚠️ Die Maße des Prompts, gezählt in promptMass.js — derselben
                   Stelle, aus der auch die Reflexionsseite ihre Zahlen nimmt.
                   Eine zweite Zählung hier wäre eine zweite Wahrheit über
                   dieselbe Sache, und genau das ist am 10.08. schon einmal
                   schiefgegangen. `content` liegt hier ohnehin vor, also
                   braucht es dafür keinen Backend-Weg. -->
              <template v-if="v.content">
                ·
                <BaseTooltip :text="HILFE_REGELN" :oben="false" fixiert>
                  <span
                      class="cursor-help"
                      :class="regelAnzahl(v.content) >= REGELN_WARN ? 'text-amber-800 font-semibold' : ''"
                  >{{ regelAnzahl(v.content) }} Regeln</span>
                </BaseTooltip>
                ·
                <BaseTooltip :text="HILFE_ZEICHEN" :oben="false" fixiert>
                  <span
                      class="cursor-help"
                      :class="promptZeichen(v.content) >= PROMPT_WARN_ZEICHEN ? 'text-amber-800 font-semibold' : ''"
                  >{{ promptZeichen(v.content) }} Zeichen</span>
                </BaseTooltip>
              </template>
            </div>

            <!-- Technisch abgebrochener Lauf. MUSS vor dem Kennzahlenblock
                 stehen und ihn ausschließen: gate_metrics trägt dann kein
                 changed_share, kein n_cells und keine reasons, sondern nur
                 error/note. Der Kennzahlenblock rendert daraus eine leere
                 Tabelle und "  gemeinsame Zellen" — und der Grund, der
                 sorgfältig formuliert daneben steht, käme nirgends an. -->
            <div
                v-if="v.gate_metrics && v.gate_metrics.error"
                class="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800"
            >
              <div class="font-semibold mb-1">Der Vergleich ist abgebrochen</div>
              <p class="text-xs leading-relaxed">
                {{ v.gate_metrics.note || "Es wurde nichts gemessen." }}
              </p>
              <pre class="mt-2 p-2 bg-white border border-red-200 rounded text-xs font-mono whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{{ v.gate_metrics.error }}</pre>
              <p class="text-xs mt-2">
                Das ist <strong>keine Aussage über den Prompt</strong> — die Version steht
                wieder auf <code>draft</code> und kann erneut verglichen werden.
              </p>
            </div>

            <!-- Von Hand abgebrochener Lauf. MUSS aus demselben Grund wie der
                 Fehlerblock darüber vor dem Kennzahlenblock stehen: auch hier
                 trägt gate_metrics kein changed_share und keine reasons.
                 Bewusst neutral und nicht rot — ein Abbruch, den jemand selbst
                 ausgelöst hat, ist kein Fehler. -->
            <div
                v-else-if="v.gate_metrics && v.gate_metrics.cancelled"
                class="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900"
            >
              <div class="font-semibold mb-1">Der Vergleich wurde abgebrochen</div>
              <p class="text-xs leading-relaxed">
                {{ v.gate_metrics.note || "Es wurde nichts gemessen." }}
              </p>
              <p v-if="v.gate_metrics.cancelled_at" class="text-xs mt-2 text-amber-700">
                angehalten {{ formatTs(v.gate_metrics.cancelled_at) }}
              </p>
            </div>

            <!-- Gate-Kennzahlen -->
            <div v-else-if="v.gate_metrics" class="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto">
              <div class="text-xs font-semibold text-gray-800 mb-2">
                Regressionsvergleich vom {{ formatTs(v.gate_run_at) }}
                <span v-if="v.gate_baseline_version_id" class="font-normal text-gray-500">
                  gegen
                  <template v-if="versionLabelById[v.gate_baseline_version_id]">
                    v{{ versionLabelById[v.gate_baseline_version_id] }}
                  </template>
                  <template v-else>
                    eine nicht mehr gelistete Version (id {{ v.gate_baseline_version_id }})
                  </template>
                </span>
                <!-- ⚠️ WORÜBER gemessen wurde. `set_name` steht seit jeher in
                     jeder gate_metrics (gate_runner.py:838), stand aber
                     nirgends auf dieser Seite — und seit R7.10 laufen
                     verschiedene Versionen gegen verschiedene Sätze: von fünf
                     Sätzen führt EINER Korrekturen, die übrigen vier null.
                     Zwei Kennzahlkarten stehen damit untereinander in
                     derselben Liste und bedeuten Verschiedenes, ohne dass es
                     dransteht. Die Zielgrößen darunter sind ohne diese Angabe
                     schlicht nicht deutbar: leer heißt dann entweder "hier war
                     nichts zu holen" oder "hier wurde am falschen Satz
                     gemessen", und die beiden sehen gleich aus. -->
                <span v-if="v.gate_metrics.set_name" class="font-normal text-gray-500">
                  · Satz <code>{{ v.gate_metrics.set_name }}</code>
                </span>
              </div>

              <!-- Ein gerissenes Einsammeln der Referenzzellen. Es wird eigens
                   mitgeschrieben (gate_runner.py:832), weil ein leergelaufenes
                   Einsammeln sonst aussähe wie ein Satz ohne Korrekturen — und
                   das sind entgegengesetzte Auskünfte. Bis hierher stand der
                   Vermerk in der Datenbank und in keiner Anzeige, womit genau
                   die Verwechslung blieb, gegen die er gebaut wurde. -->
              <p
                  v-if="v.gate_metrics.human_reference_error"
                  class="mb-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              >
                Die Korrekturen ließen sich nicht einsammeln — die
                Annäherungs-Kennzahlen fehlen deshalb, obwohl der Satz welche
                führen kann.
                <span class="font-mono">{{ v.gate_metrics.human_reference_error }}</span>
              </p>

              <table class="w-full text-xs font-mono">
                <thead>
                  <tr class="border-b border-gray-300">
                    <th class="text-left py-1 pr-4">Kennzahl</th>
                    <th class="text-right py-1 px-2">Wert</th>
                    <th class="text-right py-1 pl-2">Grenze</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in metricRows(v.gate_metrics)" :key="row.name" class="border-t border-gray-200">
                    <td class="py-1 pr-4">
                      <!-- fixiert: die Kennzahlen liegen in einem
                           overflow-x-auto, und das beschneidet auch nach oben.
                           Ein absolut gesetzter Tooltip wäre hier unsichtbar. -->
                      <BaseTooltip :text="row.hilfe" fixiert>
                        <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                          {{ row.name }}
                        </span>
                      </BaseTooltip>
                    </td>
                    <td
                        class="text-right py-1 px-2"
                        :class="row.exceeded ? 'text-red-700 font-semibold' : 'text-gray-800'"
                    >
                      {{ row.value }}
                    </td>
                    <td class="text-right py-1 pl-2 text-gray-500">{{ row.limit }}</td>
                  </tr>

                  <!-- Die abgeleiteten Kennzahlen, eingeklappt.
                       ACHTUNG, warum ein Knopf und kein <details> wie auf der
                       Reflektionsseite: die Zeilen gehören in DIESELBE
                       Tabelle. Ein <details> ist als Kind von <tbody> kein
                       gültiges HTML, und eine zweite Tabelle darunter rechnet
                       ihre Spaltenbreiten eigenständig aus — die Wertespalten
                       flüchteten dann nicht mehr, und genau das Vergleichen
                       von Zahlen untereinander ist der Zweck der Tabelle. Das
                       Erscheinungsbild bleibt dasselbe: drehendes Chevron,
                       unterstrichener Text, Anzahl in Klammern. -->
                  <tr v-if="abgeleiteteRows(v.gate_metrics).length" class="border-t border-gray-200">
                    <td colspan="3" class="py-1">
                      <button
                          type="button"
                          class="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                          :aria-expanded="zeigtAbgeleitete(v) ? 'true' : 'false'"
                          @click="toggleAbgeleitete(v)"
                      >
                        <ChevronRightIcon
                            class="w-3.5 h-3.5 shrink-0 transition-transform"
                            :class="zeigtAbgeleitete(v) ? 'rotate-90' : ''"
                            aria-hidden="true"
                        />
                        <span class="underline">
                          Bausteine und Umrechnungen ({{ abgeleiteteRows(v.gate_metrics).length }})
                        </span>
                      </button>
                    </td>
                  </tr>

                  <tr
                      v-for="row in (zeigtAbgeleitete(v) ? abgeleiteteRows(v.gate_metrics) : [])"
                      :key="'abgeleitet-' + row.name"
                      class="border-t border-gray-100"
                  >
                    <td class="py-1 pr-4 pl-4">
                      <BaseTooltip :text="row.hilfe" fixiert>
                        <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2 text-gray-500">
                          {{ row.name }}
                        </span>
                      </BaseTooltip>
                    </td>
                    <td
                        class="text-right py-1 px-2"
                        :class="row.exceeded ? 'text-red-700 font-semibold' : 'text-gray-600'"
                    >
                      {{ row.value }}
                    </td>
                    <td class="text-right py-1 pl-2 text-gray-500">{{ row.limit }}</td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-2 text-xs text-gray-600">
                <BaseTooltip :text="zellenHilfe(v.gate_metrics)" :oben="false" fixiert>
                  <span class="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                    <span :class="cellsTooFew(v.gate_metrics) ? 'text-red-700 font-semibold' : ''"
                    >{{ v.gate_metrics.n_cells }} gemeinsame Zellen</span>,
                    davon {{ v.gate_metrics.n_both_delivered }} in beiden Läufen geliefert
                  </span>
                </BaseTooltip>
              </div>

              <ul
                  v-if="(v.gate_metrics.reasons || []).length"
                  class="mt-2 list-disc pl-5 text-xs text-red-700 space-y-0.5"
              >
                <li v-for="(grund, i) in v.gate_metrics.reasons" :key="i">{{ grund }}</li>
              </ul>
            </div>

            <!-- Ohne Kennzahlen bleibt eine Zeile stehen. Der ganze Satz dazu
                 hängt am Statusabzeichen darüber — er erklärt einen
                 Zustand, und der Ort, an dem man ihn sucht, ist der Zustand
                 selbst. -->
            <div v-else class="mt-2 text-xs text-gray-500">
              {{ statusKurz(v.gate_status) }}
            </div>

            <!-- Der Inhalt, ausklappbar -->
            <button
                type="button"
                class="mt-2 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
                :aria-expanded="expanded[v.id] ? 'true' : 'false'"
                @click="toggleContent(v.id)"
            >
              <ChevronRightIcon
                  class="w-3.5 h-3.5 transition-transform"
                  :class="expanded[v.id] ? 'rotate-90' : ''"
              />
              <span class="underline">{{ expanded[v.id] ? "Text verbergen" : "Text anzeigen" }}</span>
            </button>
            <pre
                v-if="expanded[v.id]"
                class="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs font-mono whitespace-pre-wrap break-words max-h-72 overflow-y-auto"
            >{{ v.content }}</pre>
          </div>

          <!-- Aktion -->
          <div class="shrink-0 md:w-64 md:text-right flex flex-col items-stretch md:items-end gap-2">
            <!-- ACHTUNG, der Grund für den sichtbaren Satz ganz unten: ein
                 deaktivierter Knopf verschluckt in Chrome und Firefox jedes
                 Mausereignis, und es blubbert auch nicht zur Hülle hoch. Ein
                 Tooltip -- egal ob nativ oder eigener -- erscheint dort also
                 NIE. Genau dann braucht ihn aber jemand: der gesperrte Knopf
                 ist die Frage "warum nicht?". Deshalb trägt der Tooltip hier
                 nur den Normalfall, und der Sperrgrund steht als Text da. -->
            <BaseTooltip
                v-if="!v.is_active"
                text="Schaltet diese Version aktiv. Ab dann bewertet der RatingWorker jede neue Nachricht mit ihr; die bisher aktive Version wird abgelöst."
                wrapper-class="w-full md:w-auto"
            >
              <BaseButton
                  class="w-full"
                  :variant="v.activation_block_reason ? 'disabled' : 'switch'"
                  :disabled="!!v.activation_block_reason || activatingId === v.id"
                  @click="askActivate(v)"
              >
                {{ activatingId === v.id ? "Aktiviere…" : "Aktivieren" }}
              </BaseButton>
            </BaseTooltip>

            <!-- Der Vergleich. Ohne ihn kommt eine Version nie auf 'passed',
                 und der Aktivierknopf darüber bleibt dauerhaft gesperrt — bis
                 R6 war das nur auf der Kommandozeile aufzulösen. -->
            <BaseTooltip
                v-if="canCompare(v)"
                text="Misst diese Version gegen die aktive. Fährt dafür ZWEI vollständige Bewertungsläufe über den eingefrorenen Vergleichssatz — das dauert Minuten und belastet den Rating-Service. An den Bewertungen der Nachrichten ändert sich dabei nichts."
                wrapper-class="w-full md:w-auto"
            >
              <BaseButton
                  class="w-full"
                  :variant="comparingId === v.id ? 'disabled' : 'primary'"
                  :disabled="comparingId === v.id"
                  @click="askCompare(v)"
              >
                {{ comparingId === v.id ? "Starte…" : "Vergleich starten" }}
              </BaseButton>
            </BaseTooltip>

            <!-- Nur bei 'running'. Läuft der Vergleich noch, hält ihn dieser
                 Knopf an — das Backend meldet zurück, wenn das Signal ihn
                 nicht erreicht (anderer Prozess, Kommandozeile). -->
            <BaseTooltip
                v-if="v.gate_status === 'running'"
                text="Hält einen laufenden Vergleich an. Er beendet die gerade begonnene Nachricht noch — das dauert bis zu 90 s (RATING_API_TIMEOUT). Danach steht die Version wieder auf draft; gemessen wurde nichts. Fährt den Lauf gerade die Kommandozeile, erreicht ihn das Signal nicht — das Backend sagt es dann."
                wrapper-class="w-full md:w-auto"
            >
              <BaseButton
                  class="w-full"
                  :variant="cancellingId === v.id ? 'disabled' : 'primary'"
                  :disabled="cancellingId === v.id"
                  @click="askCancel(v)"
              >
                {{ cancellingId === v.id ? "Halte an…" : "Vergleich abbrechen" }}
              </BaseButton>
            </BaseTooltip>

            <!-- Ebenfalls nur bei 'running'. Ein Lauf, der mit seinem Prozess
                 gestorben ist, hinterlässt diesen Zustand und sperrt die
                 Version sonst dauerhaft. Das Backend lehnt ab, solange es den
                 Lauf noch selbst fährt.
                 Beide Knöpfe stehen nebeneinander, weil die Oberfläche die
                 beiden Fälle nicht unterscheiden kann — das weiß nur der
                 Dienst, der den Lauf gestartet hat. Jede Absage nennt den
                 jeweils anderen Weg. -->
            <BaseTooltip
                v-if="v.gate_status === 'running'"
                text="Nur richtig, wenn der Lauf mit seinem Prozess abgestürzt ist. Gibt die Version wieder frei; gemessen wurde in keinem Fall etwas, der Vergleich muss danach neu gestartet werden. Solange das Backend den Lauf selbst fährt, lehnt es ab."
                wrapper-class="w-full md:w-auto"
            >
              <BaseButton
                  class="w-full"
                  :variant="releasingId === v.id ? 'disabled' : 'delete'"
                  :disabled="releasingId === v.id"
                  @click="askRelease(v)"
              >
                {{ releasingId === v.id ? "Setze zurück…" : "Vergleich zurücksetzen" }}
              </BaseButton>
            </BaseTooltip>

            <p
                v-if="v.gate_status === 'running'"
                class="text-xs text-gray-500 md:text-right"
            >
              Läuft er noch? <strong>Abbrechen</strong>. Mit seinem Prozess
              gestorben? <strong>Zurücksetzen</strong>.
            </p>

            <p
                v-if="v.activation_block_reason && !v.is_active"
                class="text-xs text-gray-500 mt-1 md:text-right"
            >
              {{ v.activation_block_reason }}
            </p>
          </div>
        </div>
      </li>
    </ul>

    <!-- Der Leertext gilt nur, wenn das Laden auch geklappt hat. Sonst stünden
         "Fehler" und "es gibt nichts" nebeneinander, und der Nutzer müsste raten. -->
    <p v-if="!loading && !error && versions.length === 0" class="text-gray-500 mt-4 text-center">
      Noch keine Prompt-Versionen. Sie entstehen beim Speichern eines Prompts in
      <router-link to="/async/messages" class="underline">Messages → Edit Prompt</router-link>
      — sofern der Text nicht schon einmal als Version gespeichert war.
    </p>

    <!-- Ein Modal für drei Handlungen. Titel, Text und Knopfbeschriftung
         hängen an pendingAction — drei getrennte Modals wären dreimal
         dieselbe Mechanik mit drei Gelegenheiten, sie unterschiedlich
         falsch zu machen. -->
    <ConfirmModal
        :isOpen="showConfirm"
        :title="confirmTitle"
        :message="confirmMessage"
        :confirmLabel="confirmLabel"
        cancelLabel="Abbrechen"
        @confirm="confirmed"
        @cancel="cancelConfirm"
    >
      <!-- ⚠️ Der Slot ERSETZT die Meldung (ConfirmModal: <slot>{{ message }}</slot>),
           deshalb steht sie hier selbst drin. Ohne diese Zeile verschwände der
           Text aller vier Handlungen, sobald hier irgendetwas eingehängt wird —
           und das fiele bei drei von ihnen erst im Browser auf. -->
      <p>{{ confirmMessage }}</p>

      <!-- Die Satzwahl, und zwar VOR dem Start. Bis R7.10 nannte die Seite den
           Vergleichssatz erst in der Antwort — also nach dem Start, sechs
           Minuten bevor das Ergebnis kam. Wer eine von Hand angelegte Version
           gegen „default" maß, erfuhr erst dann, dass es dort nichts zu messen
           gab. -->
      <div v-if="pendingAction === 'compare'" class="mt-4 border-t border-gray-200 pt-4">
        <label class="block text-sm font-semibold text-gray-700 mb-1" for="vergleichssatz">
          Vergleichssatz
        </label>

        <select
            v-if="satzAuswahl.length"
            id="vergleichssatz"
            v-model="gewaehlterSatz"
            class="w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white"
        >
          <option v-for="s in satzAuswahl" :key="s.set_name" :value="s.set_name">
            {{ s.label }}
          </option>
        </select>

        <!-- Ohne geladene Vorschau wird NICHTS behauptet. Eine leere Auswahl
             mit einer Null daneben wäre eine Aussage über die Daten, die in
             Wahrheit eine über das Netz ist. -->
        <p v-else class="text-sm text-gray-600">
          {{ satzVorschauLaeuft
              ? "Die Vergleichssätze werden geladen…"
              : "Der Vergleichssatz wird beim Start aus der Herkunft dieser Version bestimmt." }}
        </p>

        <div
            v-if="satzHinweis"
            class="mt-3 rounded p-2 text-xs"
            :class="satzHinweis.warnung
                ? 'bg-amber-50 border border-amber-200 text-amber-900'
                : 'bg-gray-50 border border-gray-200 text-gray-600'"
        >
          {{ satzHinweis.text }}
        </div>
      </div>
    </ConfirmModal>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseTooltip from "@/BaseComponents/BaseTooltip.vue";
import ConfirmModal from "@/BaseComponents/ConfirmModal.vue";
import {
  ArrowPathIcon,
  ChevronRightIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";
import {
  listPromptVersions,
  getEffectivePromptKey,
  getComparePreview,
  activatePromptVersion,
  comparePromptVersion,
  releasePromptVersion,
  cancelPromptVersion,
} from "../api/promptVersions";
// Die Maße eines Prompts kommen aus EINEM Modul, das sich diese Seite mit der
// Reflexionsseite teilt. Zwei Zählungen für dieselbe Sache wären zwei
// Wahrheiten, und die eine liefe irgendwann anders als die andere.
import {
  HILFE_REGELN,
  HILFE_ZEICHEN,
  REGELN_WARN,
  PROMPT_WARN_ZEICHEN,
  regelAnzahl,
  promptZeichen,
} from "../promptMass";

// Wie oft nachgefragt wird, solange ein Vergleich läuft. Der Lauf dauert
// Minuten, nicht Sekunden -- häufiger zu fragen brächte nichts als Last.
const POLL_MS = 5000;

// Welche Kennzahlen angezeigt werden und gegen welche Grenze sie laufen.
// Die Grenzen stehen in gate_metrics.thresholds -- sie wandern bei jedem
// Vergleich mit, damit später nachvollziehbar ist, wogegen geprüft wurde.
//
// 'hilfe' ist die Uebersetzung des Spaltennamens in einen Satz. Die Namen
// kommen aus app/services/prompt_gate.py und sind dort exakt definiert; wer
// sie hier zum ersten Mal sieht, kann aus "missing_share_baseline" nicht
// erraten, worauf sich der Anteil bezieht. Genau das entscheidet aber, ob
// eine Zahl beunruhigend ist oder nicht.
//
// 'abgeleitet' staffelt die Anzeige, es KUERZT sie nicht: elf Zeilen sind
// nicht elf Aussagen. Nachgerechnet ueber die vier gefahrenen Laeufe ist mae
// gleich changed_share mal der mittleren Schrittweite (Faktor 1,13 · 1,33 ·
// 1,36 · 1,50), approach_share das Spiegelbild von human_gap_delta, solange
// jede Bewegung genau einen Skalenschritt gross ist (v8: -0,050 gegen
// +0,050), und die beiden missing_share_* sind die Summanden von
// missing_delta. Wer elf Zeilen gleichrangig zeigt, behauptet elf Messungen.
//
// ACHTUNG, was hier NICHT passiert: keine dieser Kennzahlen verschwindet.
// Die Oberflaeche ist ein Werkzeug, keine Folie -- wer eine Zahl sucht, die
// in gate_metrics steht, muss sie finden. Sie stehen eingeklappt darunter,
// und jede sagt in ihrer 'hilfe', wovon sie abgeleitet ist; sonst liest sich
// "eingeklappt" als "unwichtig", und das ist es nicht.
const KENNZAHLEN = [
  {
    name: "changed_share",
    limit: "max_changed_share",
    hilfe:
      "Anteil der Zellen, deren Wert sich zwischen den beiden Läufen unterscheidet — " +
      "gerechnet nur über die Zellen, die BEIDE Läufe geliefert haben.\n\n" +
      "Die Hauptkennzahl: viel Änderung heißt viel Wirkung und damit viel Risiko.\n\n" +
      "ACHTUNG: Rauschen ist NICHT der Grund für die großzügige Schwelle. Die " +
      "Bewertung läuft mit Temperatur 0, also ohne Sampling — eine Abweichung ist " +
      "Prompt-Wirkung. Was bleibt, ist der Kontext: im RAG-Pfad kann ein Ingest " +
      "zwischen den beiden Läufen die Beispiele verändern.\n\n" +
      "Die Schwelle 0,30 ist eine unkalibrierte Startmarke — in vier Läufen lag der " +
      "Wert bei 0,019 · 0,042 · 0,053 · 0,142 und hat damit noch nie getrennt.",
  },
  {
    name: "mae",
    limit: "max_mae",
    abgeleitet: true,
    hilfe:
      "Mittlerer absoluter Abstand der beiden Läufe auf der Skala −3…+3, über die " +
      "Zellen, die beidseitig eine Zahl tragen.\n\n" +
      "Ergänzt changed_share um das Ausmaß: ob sich die Werte um einen Schritt oder " +
      "um vier unterscheiden, ist dort nicht zu sehen.\n\n" +
      "≈ changed_share mal der mittleren Schrittweite; in den bisherigen Läufen " +
      "Faktor 1,1 bis 1,5. Misst gegen die VORGÄNGERVERSION, nicht gegen eine " +
      "Wahrheit — ein großer Abstand heißt 'anders als vorher', nicht 'falsch'.",
  },
  {
    name: "missing_delta",
    limit: "max_missing_delta",
    hilfe:
      "missing_share_candidate minus missing_share_baseline: um wie viel häufiger die " +
      "neue Version Zellen schuldig bleibt.\n\n" +
      "Nur nach oben geprüft — liefert der Kandidat MEHR, ist das kein Mangel. Ein " +
      "zerlegtes Antwortformat ist der teuerste Fehler einer Prompt-Änderung und hier " +
      "sofort sichtbar.\n\n" +
      "ACHTUNG: fällt eine ganze Nachricht aus, ist der Wert 1/N. Bei 16 Nachrichten " +
      "sind das 0,0625 und damit über der Schwelle 0,05 — für sie sind mindestens 20 " +
      "Nachrichten nötig.",
  },
  {
    name: "missing_share_baseline",
    limit: null,
    abgeleitet: true,
    hilfe:
      "Anteil der gemeinsamen Zellen, die die AKTIVE Version nicht geliefert hat.\n\n" +
      "Summand von missing_delta: geprüft wird der Abstand der beiden Anteile, nicht " +
      "dieser Wert. Steht hier zum Einordnen — ob 0,05 Abstand aus 0,00 gegen 0,05 " +
      "oder aus 0,40 gegen 0,45 entsteht, sieht man erst hier.",
  },
  {
    name: "missing_share_candidate",
    limit: null,
    abgeleitet: true,
    hilfe:
      "Anteil der gemeinsamen Zellen, die die NEUE Version nicht geliefert hat.\n\n" +
      "Summand von missing_delta: geprüft wird der Abstand zur aktiven Version, nicht " +
      "der absolute Wert.",
  },
  {
    name: "zero_share_baseline",
    limit: null,
    hilfe:
      "Anteil der Zellen, die die AKTIVE Version mit 0 bewertet hat — gerechnet über " +
      "ihre gelieferten Zahlenwerte.\n\n" +
      "ACHTUNG: die 0 ist dreifach belegt (neutral bewertet / Ausfall-Ersatzwert des " +
      "Modells / nicht bewertet). Deshalb wird sie getrennt von 'missing' geführt und " +
      "hat bewusst keine Grenze.",
  },
  {
    name: "zero_share_candidate",
    limit: null,
    hilfe:
      "Anteil der Zellen, die die NEUE Version mit 0 bewertet hat — gerechnet über " +
      "ihre gelieferten Zahlenwerte.\n\n" +
      "Springt der Wert gegenüber der aktiven Version, bewertet die neue Version " +
      "entweder neutraler oder fällt häufiger auf den Ersatzwert zurück. Welches von " +
      "beidem, sagt diese Zahl nicht.",
  },

  // R7. Die fünf Kennzahlen darüber sind OBERGRENZEN -- sie messen
  // Schadensfreiheit, nicht Verbesserung. Ein Kandidat, der exakt nichts
  // ändert, besteht sie alle. Damit hätte eine Reflektions-Schleife, die
  // Prompts aus menschlichen Korrekturen fortschreibt, keine RICHTUNG. Die
  // vier hier schließen die Lücke: rückt der Kandidat näher an die Korrektur,
  // und hat er dabei nur die gemeinte Stelle getroffen?
  //
  // Sie tragen bewusst KEINE Grenze (limit: null) und stehen in keinem
  // verdict()-Zweig: berichtet, nicht geurteilt. Die heutige Referenzmenge ist
  // skriptgeneriert -- eine Schwelle darauf wäre auf eine if-Abfrage
  // kalibriert --, und worauf optimiert wird, ist eine fachliche Festlegung
  // des Betreuers.
  {
    name: "n_human_cells",
    limit: null,
    hilfe:
      "Zahl der Zellen, an denen ein Mensch korrigiert hat UND beide Läufe eine Zahl " +
      "geliefert haben.\n\n" +
      "Steht bewusst vor den drei Kennzahlen darunter: ohne sie ist keine von ihnen " +
      "deutbar. Unter 10 Zellen sagt diese Zahl mehr als jede andere hier — eine " +
      "mittlere Annäherung über eine Handvoll Zellen ist keine Kennzahl.",
  },
  {
    name: "human_gap_delta",
    limit: null,
    hilfe:
      "human_gap_candidate minus human_gap_baseline: um wie viel sich der mittlere " +
      "Abstand zur menschlichen Korrektur ändert.\n\n" +
      "ACHTUNG: NEGATIV IST GUT. Das ist die einzige Kennzahl dieser Tabelle, bei der " +
      "das so ist — alle anderen sind 'kleiner ist harmloser', diese hier ist " +
      "'kleiner ist besser'. Ein negativer Wert heißt: der Kandidat liegt näher an " +
      "dem, was ein Mensch korrigiert hat.\n\n" +
      "Sie wird BERICHTET, nicht geurteilt: es gibt bewusst keine Schwelle. Die " +
      "heutige Referenzmenge ist skriptgeneriert, und worauf optimiert wird, legt der " +
      "Betreuer fest — das Instrument zuerst, das Urteil später.",
  },
  {
    name: "approach_share",
    limit: null,
    abgeleitet: true,
    hilfe:
      "(Zellen näher am Menschen minus Zellen weiter weg) geteilt durch " +
      "n_human_cells. POSITIV IST GUT.\n\n" +
      "Eine NETTO-Größe: zwei näher und eine weiter ergeben 0,25, nicht 0,67. Eine " +
      "Regel, die die eine Hälfte verbessert und die andere verschlechtert, sieht " +
      "damit nicht wie ein Gewinn aus — und genau das ist beabsichtigt.\n\n" +
      "Spiegelbild von human_gap_delta, solange sich jede Zelle um genau einen " +
      "Skalenschritt bewegt — im v8-Lauf −0,050 gegen +0,050. Eigenständig wird die " +
      "Zahl erst bei unterschiedlich großen Bewegungen: dort zählt das Ausmaß, hier " +
      "die Zahl der Zellen.",
  },
  {
    name: "collateral_share",
    limit: null,
    hilfe:
      "Anteil der geänderten Zellen AUSSERHALB der Referenzmenge.\n\n" +
      "Trennt 'hat gezielt gewirkt' von 'hat breit gestreut'. Ohne sie sagte eine " +
      "Verbesserung nichts: eine Regel, die die Referenzzellen näher rückt und dabei " +
      "alles andere umwirft, sähe genauso aus wie eine, die genau die gemeinte Stelle " +
      "trifft. changed_share deckelt die Streuung zwar, benennt sie aber nicht.",
  },
];

// Der Ablauf einer Prompt-Änderung. Stand bis zur Feinschleife als
// vierstufige Liste dauerhaft über der Versionsliste — gebraucht wird er
// beim ersten Mal, danach kostet er nur Platz. Schritt 3 (Warten) ist keine
// Handlung und deshalb in Schritt 2 aufgegangen.
const WEG = [
  {
    titel: "Entwurf",
    ziel: "/async/messages",
    hilfe:
      "Prompt in Messages → Edit Prompt speichern. Das legt einen Entwurf an und " +
      "ändert an der laufenden Bewertung noch nichts.\n\n" +
      "War derselbe Text schon einmal eine Version, entsteht keine neue Zeile — dann " +
      "taucht hier auch nichts Neues auf.\n\n" +
      "Klick öffnet Messages.",
  },
  {
    titel: "Vergleich",
    hilfe:
      "Unten bei der Version auf „Vergleich starten“. Er bewertet einen eingefrorenen " +
      "Satz Nachrichten einmal mit der aktiven und einmal mit der neuen Version; beide " +
      "Läufe entstehen frisch. Fehlt der Vergleichssatz noch, wird er beim ersten Mal " +
      "angelegt.\n\n" +
      "Zwei vollständige Bewertungsläufe dauern Minuten. Die Version steht solange auf " +
      "running, und diese Seite fragt den Stand von selbst nach.",
  },
  {
    titel: "Aktivieren",
    hilfe:
      "Besteht der Vergleich, lässt sich die Version hier aktivieren. Erst dann " +
      "bewertet der RatingWorker mit ihr.\n\n" +
      "Eine Version, die schon einmal aktiv war, kommt ohne erneuten Vergleich zurück — " +
      "ihr Verhalten ist bekannt.",
  },
];

const HILFE_KOMMANDOZEILE =
  "Ohne GATE_API_TOKEN läuft der Vergleich nicht über diese Seite. Dann:\n\n" +
  "prompt_gate.py compare --candidate-id <id>\n\n" +
  "im Container backend des rta-async-backend-Stacks — nicht in gui-backend, dort " +
  "liegt das Skript nicht.";

// R7. Woran eine Version aus der Reflektions-Schleife zu erkennen ist: ihre
// Notiz beginnt mit diesem Wort. Bewusst kein eigenes Schemafeld -- die Marke
// ist eine Anzeigehilfe, keine Zusicherung, und eine Migration dafuer waere
// unverhaeltnismaessig. Wer die Notiz von Hand aendert, verliert die Pille;
// mehr passiert nicht.
const REFLEKTIONS_MARKE = "Reflektion:";

const HILFE_REFLEKTION =
  "Diese Version ist aus einem Regelvorschlag entstanden, den der Optimierer aus " +
  "menschlichen Korrekturen gebildet hat. Welche Korrekturen das waren, steht auf " +
  "der Reflektionsseite.\n\n" +
  "Klick öffnet sie.";

// Warum diese Seite überhaupt sagen muss, welcher Schlüssel wirkt.
const HILFE_WIRKSAM =
  "Es gibt zwei Bewertungsprompts, und welcher gilt, entscheidet der Bewertungsmodus " +
  "(system_settings.async_rating_mode): llm_prompt_template im Modus LLM, " +
  "rag_prompt_template im Modus RAG, im Modus ML gar keiner.\n\n" +
  "Der Liste ist das nicht anzusehen — sie führt beide Schlüssel nebeneinander. Eine " +
  "Version des anderen lässt sich vergleichen, freigeben und aktivieren: alles läuft " +
  "durch, und an der laufenden Bewertung ändert sich kein Zeichen.\n\n" +
  "Der Modus wird in Messages → Edit Prompt umgestellt.";

// Modus ML: es gibt bauartbedingt keinen Prompt. Das ist kein Mangel, und der
// Text sagt das ausdrücklich -- sonst liest sich die Zeile wie ein Ausfall.
const HILFE_MODUS_ML =
  "Im Modus ML bewertet ein trainiertes Modell ohne Prompt. Keine der Versionen unten " +
  "wirkt derzeit — auch nicht die aktiven. Sie sind nicht verloren: sobald der Modus " +
  "wieder auf LLM oder RAG steht, gilt die dort aktive Version unverändert.";

const HILFE_BOOTSTRAP =
  "Warum nur über die Kommandozeile: der Regressionsvergleich misst gegen eine aktive " +
  "Version. Die allererste hat keine, gegen die sie gemessen werden könnte — sie ist " +
  "eine Setzung, keine Messung, und die gehört nicht hinter einen Knopf.\n\n" +
  "Der Text wird aus system_settings übernommen. Steht dort keiner, erst unten einen " +
  "Entwurf anlegen und dessen id mit --from-version-id übergeben.\n\n" +
  "Der Container heißt backend und gehört zum rta-async-backend-Stack — in " +
  "gui-backend liegt das Skript nicht.";

// Was ein gate_status bedeutet. Kurzform steht in der Zeile, der ganze Satz
// hängt am Abzeichen: wer einen Zustand nicht kennt, sucht die Erklärung
// am Zustand selbst.
const STATUS_HILFE = {
  draft: {
    kurz: "Noch kein Vergleich gefahren.",
    lang:
      "Entwurf. Der Text liegt gespeichert vor, aber es wurde nie gemessen, was er " +
      "gegenüber der aktiven Version tut — und ohne Messung keine Aktivierung.\n\n" +
      "Nächster Schritt: „Vergleich starten“.",
  },
  running: {
    kurz: "Ein Vergleich läuft — der Stand aktualisiert sich von selbst.",
    lang:
      "Ein Vergleich läuft gerade. Solange bleibt die Version gesperrt; diese Seite " +
      "fragt den Stand alle paar Sekunden nach.\n\n" +
      "Bricht der Lauf mit seinem Prozess ab, bleibt dieser Zustand stehen — dann gibt " +
      "„Vergleich zurücksetzen“ die Version wieder frei.",
  },
  passed: {
    kurz: "Der Vergleich ist bestanden.",
    lang:
      "Der Vergleich ist bestanden — die Version lässt sich aktivieren.\n\n" +
      "ACHTUNG: die Freigabe kann verfallen. Gemessen wurde gegen die Version, die " +
      "damals aktiv war; wird zwischenzeitlich eine andere aktiviert, ist der " +
      "Vergleich nicht mehr gültig und muss neu gefahren werden.",
  },
  failed: {
    kurz: "Nicht bestanden.",
    lang:
      "Der Vergleich ist nicht bestanden. Die Gründe stehen bei den Kennzahlen, " +
      "solange sie da sind — sie werden geräumt, sobald derselbe Prompt erneut als " +
      "Entwurf gespeichert wird.\n\n" +
      "Ein neuer Versuch ist der Normalfall: „Vergleich starten“ geht wieder.",
  },
  baseline: {
    kurz: "Referenzversion, ohne Vergleichspartner.",
    lang:
      "Die Referenzversion — die erste ihres Schlüssels. Sie hatte nichts, wogegen sie " +
      "gemessen werden konnte, deshalb gibt es hier keine Kennzahlen.\n\n" +
      "Sie ist per Kommandozeile gesetzt worden und lässt sich nicht vergleichen; das " +
      "Backend lehnt das ausdrücklich ab.",
  },
};

export default {
  components: {
    BaseButton,
    BaseTooltip,
    ConfirmModal,
    ArrowPathIcon,
    ChevronRightIcon,
    InformationCircleIcon,
  },

  data() {
    return {
      // Konstanten, die das Template braucht. Sie stehen als Modulkonstanten
      // oben und werden hier nur durchgereicht — Fließtext gehört nicht
      // zwischen die Zustandsfelder.
      WEG,
      HILFE_KOMMANDOZEILE,
      HILFE_BOOTSTRAP,
      HILFE_REFLEKTION,
      HILFE_WIRKSAM,
      HILFE_MODUS_ML,
      HILFE_REGELN,
      HILFE_ZEICHEN,
      REGELN_WARN,
      PROMPT_WARN_ZEICHEN,
      POLL_MS,

      versions: [],
      // Welcher Schlüssel gerade bewertet — {rating_mode, prompt_key, ...}
      // oder null, solange es noch nicht geladen ist. NICHT vorbelegen: ein
      // geratener Vorgabewert stünde als Tatsachenbehauptung auf der Seite,
      // bis die Antwort da ist, und im Modus ML wäre er dauerhaft falsch.
      wirksam: null,
      promptKey: "",
      loading: false,
      error: "",
      savedMessage: "",
      activatingId: null,
      comparingId: null,
      releasingId: null,
      cancellingId: null,
      expanded: {},
      // Je Version: hat der Nutzer die abgeleiteten Kennzahlen auf- oder
      // zugeklappt? Ein fehlender Eintrag heisst "nicht entschieden" und ist
      // NICHT dasselbe wie false -- siehe zeigtAbgeleitete.
      abgeleitetOffen: {},
      showConfirm: false,
      pendingVersion: null,
      // "activate" | "compare" | "release" | "cancel" -- steuert Titel, Text und
      // Knopfbeschriftung des einen Modals.
      pendingAction: null,
      pollTimer: null,

      // Die Satzwahl des Vergleichsdialogs (R7.10).
      //
      // ⚠️ satzVorschau bleibt null, solange nichts geladen ist, und wird NICHT
      // vorbelegt. Ein geratener Vorgabesatz stünde als Tatsachenbehauptung im
      // Dialog ("gemessen wird gegen X"), bis die Antwort da ist — dieselbe
      // Regel wie bei `wirksam` oben.
      satzVorschau: null,
      satzVorschauLaeuft: false,
      // "" heißt: keine eigene Wahl getroffen. Dann geht `null` ans Backend und
      // es entscheidet aus der Herkunft der Version — genau wie vor R7.10.
      gewaehlterSatz: "",
    };
  },

  computed: {
    // Welche Schlüssel überhaupt in der Liste vorkommen und ob dort eine
    // aktive Zeile dabei ist. Ohne aktive Version ist die Kette blockiert --
    // das muss oben stehen und nicht erst am gesperrten Knopf auffallen.
    keysWithoutActive() {
      const gesehen = new Map();
      for (const v of this.versions) {
        gesehen.set(v.prompt_key, gesehen.get(v.prompt_key) || v.is_active);
      }
      // Ein Schlüssel ohne jede Zeile taucht hier nicht auf. Er ist trotzdem
      // ohne Baseline -- deshalb wird bei leerer Liste nichts behauptet, und
      // die Warnung erscheint erst, wenn es Versionen ohne aktive gibt.
      return [...gesehen.entries()].filter(([, aktiv]) => !aktiv).map(([k]) => k);
    },

    hasActiveVersion() {
      if (!this.versions.length) return true; // leere Liste: siehe Leertext
      return this.keysWithoutActive.length === 0;
    },

    missingBaselineLabel() {
      return this.keysWithoutActive.join(" und ") || "diesen Schlüssel";
    },

    // Der Schlüssel, der in den Bootstrap-Befehl gehört.
    //
    // ⚠️ Der ERSTE ohne aktive Version, nicht der wirksame: die Warnung
    // erscheint genau dann, wenn ein gelisteter Schlüssel keine aktive Zeile
    // hat, und der Befehl muss denselben meinen. Fehlen beide, führt der
    // Befehl den ersten -- der zweite Aufruf steht dann in der Warnung, die
    // danach stehen bleibt.
    bootstrapSchluessel() {
      return this.keysWithoutActive[0] || "llm_prompt_template";
    },

    // Welcher Prompt gerade bewertet -- null im Modus ML UND solange die
    // Auskunft nicht geladen ist. Die beiden Fälle werden im Template
    // getrennt (v-if="wirksam"), nicht hier: hier wären sie ununterscheidbar.
    wirksamerSchluessel() {
      return this.wirksam?.prompt_key || null;
    },

    // Von id auf Versionsnummer, damit "gegen Version N" nicht zwei
    // Zahlenräume mischt: gate_baseline_version_id ist eine id, v.version die
    // menschenlesbare Nummer.
    versionLabelById() {
      const nach = {};
      for (const v of this.versions) nach[v.id] = v.version;
      return nach;
    },

    // Läuft irgendwo ein Vergleich? Solange ja, fragt die Seite nach.
    hasRunningComparison() {
      return this.versions.some((v) => v.gate_status === "running");
    },

    confirmTitle() {
      switch (this.pendingAction) {
        case "compare":
          return "Regressionsvergleich starten";
        case "release":
          return "Vergleich zurücksetzen";
        case "cancel":
          return "Vergleich abbrechen";
        default:
          return "Prompt-Version aktivieren";
      }
    },

    confirmLabel() {
      switch (this.pendingAction) {
        case "compare":
          return "Starten";
        case "release":
          return "Zurücksetzen";
        case "cancel":
          // NICHT "Abbrechen": der Verwerfen-Knopf daneben heißt so, und zwei
          // gleich beschriftete Knöpfe in einem Dialog sind eine Falle.
          return "Anhalten";
        default:
          return "Aktivieren";
      }
    },

    // Die wählbaren Vergleichssätze, mit ihrer Beschriftung.
    //
    // ⚠️ Der Vorgabesatz steht auch dann drin, wenn ihn die Liste nicht führt.
    // Ein Satz, den es noch nicht gibt, ist ein gültiges Ziel: `start_compare`
    // friert einen leeren Satz beim ersten Vergleich selbst ein. Fiele er aus
    // der Auswahl, könnte der Dialog ausgerechnet die Vorgabe nicht anbieten —
    // und die Seite widerspräche dem, was der Start tut.
    satzAuswahl() {
      const vorschau = this.satzVorschau;
      if (!vorschau) return [];
      const vorgabe = vorschau.default_set_name || "";
      const liste = (vorschau.sets || []).map((s) => ({ ...s, bekannt: true }));
      if (vorgabe && !liste.some((s) => s.set_name === vorgabe)) {
        liste.unshift({ set_name: vorgabe, bekannt: false });
      }
      return liste.map((s) => ({ ...s, label: this.satzLabel(s) }));
    },

    // Der gerade gewählte Eintrag — oder undefined, solange nichts geladen ist.
    gewaehlteSatzInfo() {
      return this.satzAuswahl.find((s) => s.set_name === this.gewaehlterSatz);
    },

    // Was über die gewählte Menge zu sagen ist — und zwar NUR, was nicht schon
    // in der Auswahl steht. Der Normalfall bekommt deshalb keinen Text: Name,
    // Zellenzahl und Nachrichtenzahl führt bereits das Option-Label
    // (`satzLabel`), ein Absatz darunter schriebe dieselbe Auskunft ein zweites
    // Mal aus. `warnung` färbt — und steht NUR, wenn wirklich null
    // Referenzzellen gezählt wurden.
    //
    // ⚠️ „nicht erfragbar" ist kein Warnfall. Die Zahl fehlt dann, weil das
    // Bewertungs-Backend nicht geantwortet hat; sie als 0 zu zeigen wäre eine
    // Aussage über die Daten, die in Wahrheit eine über das Netz ist.
    satzHinweis() {
      if (this.pendingAction !== "compare") return null;
      const s = this.gewaehlteSatzInfo;
      if (!s) return null;

      if (!s.bekannt) {
        const fehler = this.satzVorschau?.sets_error;
        return {
          warnung: false,
          text: fehler
              ? `Wie viele Referenzzellen „${s.set_name}" führt, ließ sich nicht ` +
                `erfragen: ${fehler} Der Vergleich läuft trotzdem — nur diese ` +
                `Vorschau fehlt.`
              : `Den Satz „${s.set_name}" gibt es noch nicht. Er wird beim Start ` +
                `eingefroren, ohne Rücksicht auf Korrekturen — wie viele er dann ` +
                `führt, steht vorher nicht fest.`,
        };
      }

      if (s.n_reference_cells === 0) {
        // Die sechs Kennzahlnamen sind raus. Wer vor dem Start steht, muss
        // EINE Sache wissen -- dass „passed" hier weniger bedeutet als sonst;
        // welche drei Kennzahlen leer bleiben, steht danach in der Tabelle,
        // und jede von ihnen traegt dort ihre eigene Erklärung.
        return {
          warnung: true,
          text:
              `Der Satz „${s.set_name}" führt keine Korrekturen ` +
              `(${s.n_messages} Nachrichten, 0 davon korrigiert). Gemessen wird ` +
              `nur Schadensfreiheit: „passed" heißt dann „nichts kaputt gemacht", ` +
              `nicht „näher am Menschen".`,
        };
      }

      // Der Normalfall sagt nichts. Die Zahlen stehen im Auswahlfeld darüber.
      return null;
    },

    // Jeder Text nennt die HANDLUNG und das, was die Entscheidung ändert —
    // sonst nichts. Die Mechanik (RATING_API_TIMEOUT, eigene Gate-Tabellen, der
    // Ablehngrund des Backends) steht im Tooltip des Knopfes, über den man
    // hierher gekommen ist; sie hier zu wiederholen hieß, denselben Absatz
    // zweimal zu lesen und beim zweiten Mal zu überfliegen.
    confirmMessage() {
      const v = this.pendingVersion;
      if (!v) return "";
      if (this.pendingAction === "compare") {
        return (
          `Version ${v.version} von ${v.prompt_key} wird gegen die aktive Version ` +
          `gemessen — zwei vollständige Bewertungsläufe, das dauert einige ` +
          `Minuten. Die Bewertungen der Nachrichten bleiben unberührt.`
        );
      }
      if (this.pendingAction === "cancel") {
        return (
          `Der laufende Vergleich für Version ${v.version} wird angehalten. ` +
          `Die Version steht danach wieder auf „draft"; gemessen wurde nichts.`
        );
      }
      if (this.pendingAction === "release") {
        // Der Kommandozeilen-Fall bleibt stehen: er ist der einzige, in dem das
        // Backend NICHT schuetzen kann, und damit das einzige, was die
        // Entscheidung des Nutzers wirklich aendert.
        return (
          `Version ${v.version} steht auf „running". Zurücksetzen ist nur ` +
          `richtig, wenn der Lauf abgestürzt ist. Fährt ihn gerade jemand auf ` +
          `der Kommandozeile, merkt das Backend es nicht.`
        );
      }
      return (
        `Version ${v.version} von ${v.prompt_key} wird ab sofort für jede neue ` +
        `Bewertung verwendet. Die bisher aktive Version wird deaktiviert.`
      );
    },
  },

  mounted() {
    // Eine Meldung, die eine andere Seite mitgegeben hat. Die Reflektionsseite
    // legt einen Entwurf an und leitet sofort hierher um -- ihre eigene
    // Erfolgsmeldung wird dabei mit ihrer Komponente zerstoert, und der Nutzer
    // landet in einer Versionsliste, ohne zu wissen, welche Zeile gerade
    // entstanden ist. Die Zeichenkette wird ANGEZEIGT und nie ausgewertet.
    const mitgegeben = this.$route.query.hinweis;
    if (typeof mitgegeben === "string" && mitgegeben) {
      this.savedMessage = mitgegeben;
      // Aus der Adresse nehmen: sonst stuende die Quittung nach jedem Reload
      // erneut da und behauptete eine Handlung, die laengst vorbei ist.
      this.$router.replace({ path: this.$route.path, query: {} });
    }
    this.reload();
  },

  beforeUnmount() {
    // Ohne das liefe der Timer nach dem Verlassen der Seite weiter und
    // schriebe in eine Komponente, die es nicht mehr gibt. Das Flag zuerst:
    // ein noch laufendes reload() ruft in seinem finally syncPolling, und
    // ohne die Marke legte das gleich wieder einen neuen Timer an.
    this._zerstoert = true;
    this.stopPolling();
  },

  methods: {
    // Durchgereicht aus promptMass.js. Eine eigene Zählung hier wäre genau der
    // Fehler, den das gemeinsame Modul verhindert.
    regelAnzahl,
    promptZeichen,

    // Gehört diese Version zu einem Schlüssel, der gerade nicht bewertet?
    //
    // ⚠️ Ohne geladene Auskunft wird NICHTS behauptet. Ein `true` als
    // Rückfall setzte an jede Zeile eine Warnmarke, sobald der Aufruf
    // fehlschlägt; ein `false` ist die schwächere und damit richtige Antwort:
    // die Zeile sieht dann aus wie vor dieser Änderung.
    wirktNicht(v) {
      if (!this.wirksam) return false;
      return v.prompt_key !== this.wirksam.prompt_key;
    },

    hilfeWirktNicht(v) {
      if (!this.wirksam?.prompt_key) {
        return (
          `Der Bewertungsmodus steht auf ${this.wirksam?.rating_mode || "ML"} — dort ` +
          "wird ohne Prompt bewertet. Diese Version zu aktivieren ändert an der " +
          "laufenden Bewertung nichts."
        );
      }
      return (
        `Bewertet wird mit ${this.wirksam.prompt_key} (Modus ${this.wirksam.rating_mode}), ` +
        `diese Version gehört zu ${v.prompt_key}.\n\n` +
        "Vergleich und Freigabe laufen trotzdem durch — der Vergleich leitet seinen " +
        "Modus aus dem Schlüssel ab und misst also sauber. Nur wirksam wird das " +
        "Ergebnis nicht: aktiviert ändert diese Version an der laufenden Bewertung " +
        "kein Zeichen.\n\n" +
        "Der Modus wird in Messages → Edit Prompt umgestellt."
      );
    },

    _fehlertext(e, fallback) {
      return String(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          e?.message ||
          fallback
      );
    },

    async reload({ quiet = false } = {}) {
      // quiet: der Poll-Lauf darf die Seite nicht bei jedem Tick auf "Laden…"
      // stellen -- das flackerte alle fünf Sekunden.
      if (!quiet) this.loading = true;
      // ⚠️ Die Fehlermeldung NUR beim ausdrücklichen Laden räumen. Täte es
      // auch der stille Poll, verschwände ein 409/502 aus einem
      // fehlgeschlagenen Vergleich nach spätestens fünf Sekunden von selbst --
      // solange irgendeine Version auf 'running' steht, also genau dann, wenn
      // der Nutzer die Meldung braucht.
      if (!quiet) this.error = "";

      // Laufende Nummer gegen das Überholen. Wechselt jemand den
      // Schlüsselfilter, während ein Poll unterwegs ist, käme dessen Antwort
      // womöglich NACH der neuen und überschriebe die Liste mit der des alten
      // Filters -- und hasRunningComparison entschiede dann auf einer Liste,
      // die gar nicht mehr angezeigt werden soll.
      const meine = (this._ladenr = (this._ladenr || 0) + 1);
      try {
        // Beide Abfragen zusammen: der wirksame Schlüssel entscheidet, wie die
        // Liste zu lesen ist. Kämen sie nacheinander, stünde die Liste kurz
        // ohne diese Einordnung da — und genau in dieser Lücke sieht eine
        // Version des unwirksamen Schlüssels aus wie eine, die etwas bewirkt.
        //
        // ⚠️ Ein Fehler beim wirksamen Schlüssel darf die LISTE nicht
        // verhindern: sie ist die eigentliche Arbeitsfläche. Deshalb wird er
        // hier gefangen und nur als fehlende Einordnung sichtbar (die Marken
        // verschwinden), nicht als roter Kasten über einer leeren Seite.
        const [res, wirksam] = await Promise.all([
          listPromptVersions(this.promptKey || null),
          getEffectivePromptKey().catch(() => null),
        ]);
        if (meine !== this._ladenr) return;
        this.versions = res?.data || [];
        this.wirksam = wirksam?.data || null;
      } catch (e) {
        if (meine !== this._ladenr) return;
        this.error = this._fehlertext(e, "Prompt-Versionen konnten nicht geladen werden");
      } finally {
        if (meine === this._ladenr) {
          if (!quiet) this.loading = false;
          this.syncPolling();
        }
      }
    },

    // Wo ein Vergleich etwas ändern kann.
    //
    // Eine aktive Version ist ihr eigener Maßstab, eine laufende wird gerade
    // gemessen, und eine Baseline würde durch den Lauf ihren Sonderstatus
    // verlieren (das Backend lehnt sie ausdrücklich ab).
    //
    // ⚠️ 'passed' ist der Fall, der diese Methode überhaupt kompliziert macht.
    // Eine bestandene Version braucht normalerweise keinen zweiten Lauf — aber
    // ihre Freigabe kann VERFALLEN: activation_block_reason blockiert, sobald
    // gate_baseline_version_id nicht mehr die heute aktive Version ist. Das
    // passiert schon bei zwei Entwürfen, die beide gegen dieselbe Version
    // bestehen und von denen einer aktiviert wird — der andere steht dann auf
    // 'passed', ist nicht aktivierbar, und ohne diesen Zweig hätte er auch
    // keinen Vergleichsknopf mehr. Genau die Sackgasse, gegen die dieses
    // Paket gebaut ist, eine Ebene höher.
    //
    // 'failed' bleibt ausdrücklich drin — ein neuer Versuch ist der Normalfall.
    canCompare(v) {
      if (v.is_active) return false;
      if (v.gate_status === "running" || v.gate_status === "baseline") return false;
      if (v.gate_status === "passed") return !!v.activation_block_reason;
      return true;
    },

    syncPolling() {
      if (this.hasRunningComparison) this.startPolling();
      else this.stopPolling();
    },

    startPolling() {
      // ⚠️ Nicht mehr starten, wenn die Komponente schon weg ist. Der Weg
      // dahin ist real: beforeUnmount ruft stopPolling, aber ein zu diesem
      // Zeitpunkt noch LAUFENDES reload() ruft in seinem finally syncPolling
      // -- und legte damit ein setInterval auf einer zerstörten Komponente an,
      // das niemand mehr abräumt.
      if (this._zerstoert) return;
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => this.reload({ quiet: true }), POLL_MS);
    },

    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    askActivate(v) {
      this.pendingVersion = v;
      this.pendingAction = "activate";
      this.showConfirm = true;
    },

    askCompare(v) {
      this.pendingVersion = v;
      this.pendingAction = "compare";
      this.showConfirm = true;
      this.ladeSatzVorschau(v);
    },

    satzLabel(s) {
      if (!s.bekannt) return `${s.set_name} — Zahl unbekannt`;
      return (
        `${s.set_name} — ${s.n_reference_cells} Referenzzellen ` +
        `aus ${s.n_messages_with_reference}/${s.n_messages} Nachrichten`
      );
    },

    // Die Vorschau zu genau dieser Version holen.
    //
    // ⚠️ Mit laufender Nummer gegen das Überholen: wer den Dialog schließt und
    // gleich einen für eine andere Version öffnet, bekäme sonst womöglich die
    // Antwort des ersten — und der Dialog nennte einen Satz, der zu einer
    // anderen Version gehört. Dieselbe Falle wie in reload().
    async ladeSatzVorschau(v) {
      const meine = (this._vorschaunr = (this._vorschaunr || 0) + 1);
      this.satzVorschau = null;
      this.gewaehlterSatz = "";
      this.satzVorschauLaeuft = true;
      try {
        const res = await getComparePreview(v.id);
        if (meine !== this._vorschaunr) return;
        this.satzVorschau = res?.data || null;
        this.gewaehlterSatz = this.satzVorschau?.default_set_name || "";
      } catch (e) {
        if (meine !== this._vorschaunr) return;
        // ⚠️ Kein roter Kasten und keine Sperre. Die Vorschau ist eine
        // Zusatzauskunft; ihr Ausfall darf den Vergleich nicht verhindern.
        // Ohne sie bleibt gewaehlterSatz leer, es geht `null` ans Backend, und
        // das entscheidet aus der Herkunft — also genau wie vor R7.10.
        this.satzVorschau = null;
        this.gewaehlterSatz = "";
      } finally {
        if (meine === this._vorschaunr) this.satzVorschauLaeuft = false;
      }
    },

    askRelease(v) {
      this.pendingVersion = v;
      this.pendingAction = "release";
      this.showConfirm = true;
    },

    askCancel(v) {
      this.pendingVersion = v;
      this.pendingAction = "cancel";
      this.showConfirm = true;
    },

    cancelConfirm() {
      this.showConfirm = false;
      this.pendingVersion = null;
      this.pendingAction = null;
      this.satzwahlZuruecksetzen();
    },

    // ⚠️ Nach jedem Schließen räumen, nicht nur beim Abbrechen. Bliebe die
    // Wahl stehen, träfe sie beim nächsten Öffnen auf eine ANDERE Version —
    // und der Dialog zeigte einen Satz an, den seine Vorschau nie geliefert
    // hat. Die laufende Nummer wird mit hochgezählt, damit eine noch
    // unterwegs befindliche Antwort nicht nachträglich hineinschreibt.
    satzwahlZuruecksetzen() {
      this._vorschaunr = (this._vorschaunr || 0) + 1;
      this.satzVorschau = null;
      this.gewaehlterSatz = "";
      this.satzVorschauLaeuft = false;
    },

    // Ein Einstieg für vier Handlungen. Die Verzweigung steht hier und nicht
    // im Template, damit das Modal nicht raten muss, was "confirm" bedeutet.
    //
    // ⚠️ Kein default auf "activate". Aktivieren ist die folgenreichste der
    // vier Handlungen — sie schaltet die produktive Bewertung um. Sie als
    // Rückfall für jeden unbekannten oder bereits geleerten pendingAction zu
    // nehmen hieße: im Zweifel das Schärfste tun. Ein unbekannter Zustand
    // führt deshalb zu gar nichts.
    confirmed() {
      switch (this.pendingAction) {
        case "compare":
          return this.compareConfirmed();
        case "release":
          return this.releaseConfirmed();
        case "cancel":
          return this.cancelRunConfirmed();
        case "activate":
          return this.activateConfirmed();
        default:
          this.cancelConfirm();
          return;
      }
    },

    async compareConfirmed() {
      const v = this.pendingVersion;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!v) return;

      this.comparingId = v.id;
      this.error = "";
      this.savedMessage = "";
      // Die getroffene Wahl geht ausdrücklich mit. "" heißt "keine Wahl" und
      // wird zu null — dann entscheidet das Backend aus der Herkunft.
      //
      // ⚠️ Auch die unveränderte VORGABE wird ausdrücklich geschickt, obwohl
      // null dasselbe ergäbe. Grund: der Dialog hat gerade zugesagt, gegen
      // welchen Satz gemessen wird. Ließe man das Backend erneut ableiten,
      // könnte zwischen Anzeige und Start ein Vorschlag dazukommen und der Lauf
      // liefe gegen einen anderen Satz als den zugesagten — eine stille
      // Abweichung zwischen dem, was dastand, und dem, was passiert.
      const gewaehlt = this.gewaehlterSatz || null;
      try {
        const res = await comparePromptVersion(v.id, gewaehlt);
        const frozen = res?.data?.frozen || 0;
        // Welcher Satz gemessen wird, entscheidet seit R7.8 das Backend anhand
        // der Herkunft der Version. Der Name kommt deshalb aus der ANTWORT und
        // wird hingeschrieben: eine Version aus der Reflektion läuft gegen
        // einen anderen Satz als eine von Hand angelegte, und wer das nicht
        // sieht, kann die Kennzahlen darunter nicht einordnen.
        const satz = res?.data?.set_name;
        this.savedMessage =
          `Der Vergleich für Version ${v.version} läuft` +
          (satz ? ` — gegen den Vergleichssatz „${satz}"` : "") +
          `. Er fährt zwei ` +
          `vollständige Bewertungen und dauert einige Minuten; der Stand ` +
          `aktualisiert sich hier von selbst.` +
          (frozen ? ` (${frozen} Nachrichten als Vergleichssatz eingefroren.)` : "");
        await this.reload();
      } catch (e) {
        // 409 ist die Auskunft des Gates, 502 ein Verbindungs- oder
        // Konfigurationsfehler zwischen den Diensten. Beide Texte sind zum
        // Lesen gemacht und werden unverändert gezeigt.
        this.error = this._fehlertext(e, "Der Vergleich ließ sich nicht starten");
      } finally {
        this.comparingId = null;
        this.pendingVersion = null;
        this.satzwahlZuruecksetzen();
      }
    },

    async releaseConfirmed() {
      const v = this.pendingVersion;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!v) return;

      this.releasingId = v.id;
      this.error = "";
      this.savedMessage = "";
      try {
        await releasePromptVersion(v.id);
        this.savedMessage =
          `Version ${v.version} ist wieder freigegeben. Es wurde nichts ` +
          `gemessen — der Vergleich muss neu gestartet werden.`;
        await this.reload();
      } catch (e) {
        this.error = this._fehlertext(e, "Zurücksetzen fehlgeschlagen");
      } finally {
        this.releasingId = null;
        this.pendingVersion = null;
      }
    },

    // Heisst bewusst NICHT cancelConfirmed -- cancelConfirm() daneben schliesst
    // das Modal, und zwei fast gleiche Namen fuer Gegenteiliges sind die Sorte
    // Falle, die beim Lesen niemand sieht.
    async cancelRunConfirmed() {
      const v = this.pendingVersion;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!v) return;

      this.cancellingId = v.id;
      this.error = "";
      this.savedMessage = "";
      try {
        await cancelPromptVersion(v.id);
        // NICHT "zurückgesetzt": die Version steht noch auf 'running'. Der
        // Lauf beendet die laufende Nachricht und setzt sie danach selbst auf
        // 'draft'; das Polling zeigt den Übergang von allein.
        this.savedMessage =
          `Abbruch für Version ${v.version} angefordert. Der Lauf beendet die ` +
          `gerade laufende Nachricht noch — das kann bis zu 90 Sekunden dauern.`;
        await this.reload();
      } catch (e) {
        this.error = this._fehlertext(e, "Abbrechen fehlgeschlagen");
      } finally {
        this.cancellingId = null;
        this.pendingVersion = null;
      }
    },

    async activateConfirmed() {
      const v = this.pendingVersion;
      this.showConfirm = false;
      this.pendingAction = null;
      if (!v) return;

      this.activatingId = v.id;
      this.error = "";
      this.savedMessage = "";
      try {
        await activatePromptVersion(v.id);
        this.savedMessage = `Version ${v.version} von ${v.prompt_key} ist jetzt aktiv.`;
        setTimeout(() => {
          this.savedMessage = "";
        }, 4000);
        // Kein Optimistic Update: der lokale Stand muss dem entsprechen, was
        // das Backend tatsächlich getan hat -- die Aktivierung schaltet eine
        // zweite Zeile mit um.
        await this.reload();
      } catch (e) {
        // 409 ist keine Panne, sondern die Auskunft des Gates. Der Grund steht
        // im Klartext in detail und wird unverändert gezeigt.
        this.error = this._fehlertext(e, "Aktivieren fehlgeschlagen");
      } finally {
        this.activatingId = null;
        this.pendingVersion = null;
      }
    },

    // R7. Nur eine Anzeigemarke -- der Backend-Pfad setzt die Notiz mit
    // diesem Praefix, wenn die Version aus einem uebernommenen Vorschlag
    // entstanden ist.
    ausReflektion(v) {
      return String(v?.note || "").startsWith(REFLEKTIONS_MARKE);
    },

    toggleContent(id) {
      this.expanded = { ...this.expanded, [id]: !this.expanded[id] };
    },

    // Stehen die abgeleiteten Kennzahlen dieser Version offen?
    //
    // ACHTUNG, warum das keine schlichte Abfrage von abgeleitetOffen[id] ist:
    // mae traegt als einzige der vier eine GRENZE und geht damit in verdict()
    // ein. Waere sie fest eingeklappt, koennte ein Lauf an einer Zahl
    // scheitern, die man erst nach einem Klick sieht. Reisst eine abgeleitete
    // Kennzahl ihre Grenze, steht die Gruppe deshalb von selbst offen -- bis
    // jemand sie zuklappt, denn eine Entscheidung des Nutzers schlaegt die
    // Vorgabe.
    zeigtAbgeleitete(v) {
      const gesetzt = this.abgeleitetOffen[v.id];
      if (gesetzt !== undefined) return gesetzt;
      return this.abgeleiteteRows(v.gate_metrics).some((r) => r.exceeded);
    },

    toggleAbgeleitete(v) {
      this.abgeleitetOffen = {
        ...this.abgeleitetOffen,
        [v.id]: !this.zeigtAbgeleitete(v),
      };
    },

    // Ein unbekannter Status ist möglich — gate_status ist eine Zeichenkette
    // aus der Datenbank, keine Aufzählung im Frontend. Dann lieber nichts
    // behaupten als raten.
    statusHilfe(status) {
      return STATUS_HILFE[status]?.lang || "";
    },

    statusKurz(status) {
      return STATUS_HILFE[status]?.kurz || "";
    },

    zellenHilfe(metrics) {
      const grenze = this.minCells(metrics);
      const teile = [
        "Gemessen wird nur über Zellen, die BEIDE Läufe kennen. Eine Zelle, die nur " +
          "einer der beiden hat, sagt nichts über den Unterschied der Prompts.",
        "changed_share und mae rechnen zusätzlich nur über die beidseitig GELIEFERTEN " +
          "Zellen — sonst zählte ein Ausfall doppelt: einmal in missing_delta und noch " +
          "einmal als Veränderung.",
      ];
      if (grenze !== null) {
        teile.push(
          `Mindestens ${grenze} gemeinsame Zellen sind nötig (min_cells). Darunter gilt ` +
            "der Vergleich als nicht bestanden, unabhängig von allen anderen Zahlen — " +
            "eine Freigabe auf drei Zellen wäre eine Zahl ohne Aussage."
        );
      }
      return teile.join("\n\n");
    },

    statusClass(status) {
      switch (status) {
        case "passed":
          return "bg-green-100 text-green-700";
        case "baseline":
          return "bg-blue-100 text-blue-700";
        case "failed":
          return "bg-red-100 text-red-700";
        case "running":
          return "bg-amber-100 text-amber-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    },

    formatTs(wert) {
      if (!wert) return "—";
      const d = new Date(wert);
      if (Number.isNaN(d.getTime())) return wert;
      return d.toLocaleString();
    },

    // Die tragenden Kennzahlen, ohne Klick sichtbar.
    metricRows(metrics) {
      return this.metricRowsFuer(metrics, false);
    },

    // Die abgeleiteten: Umrechnungen und Summanden der Zeilen darüber. Sie
    // stehen eingeklappt darunter -- vollständig, nur nicht gleichrangig.
    abgeleiteteRows(metrics) {
      return this.metricRowsFuer(metrics, true);
    },

    metricRowsFuer(metrics, abgeleitet) {
      const grenzen = metrics?.thresholds || {};
      const zeilen = [];
      for (const k of KENNZAHLEN) {
        if (Boolean(k.abgeleitet) !== abgeleitet) continue;
        const wert = metrics?.[k.name];
        if (wert === undefined) continue;
        const grenze = k.limit ? grenzen[k.limit] : null;
        zeilen.push({
          name: k.name,
          hilfe: k.hilfe,
          value: this.formatZahl(wert),
          limit: grenze === undefined || grenze === null ? "—" : this.formatZahl(grenze),
          // Nur überschritten, wenn beide Zahlen sind. Ein nicht berechenbarer
          // Wert ist kein bestandener Vergleich, aber auch keine Überschreitung
          // -- er steht als Grund in reasons.
          exceeded:
            typeof wert === "number" && typeof grenze === "number" && wert > grenze,
        });
      }
      return zeilen;
    },

    minCells(metrics) {
      const wert = metrics?.thresholds?.min_cells;
      return typeof wert === "number" ? wert : null;
    },

    cellsTooFew(metrics) {
      const grenze = this.minCells(metrics);
      return typeof grenze === "number" && (metrics?.n_cells ?? 0) < grenze;
    },

    formatZahl(wert) {
      if (wert === null || wert === undefined) return "—";
      if (typeof wert !== "number") return String(wert);
      return Number.isInteger(wert) ? String(wert) : wert.toFixed(4);
    },
  },
};
</script>
